import { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft, UserPlus, CheckCircle2, Clock, XCircle, Upload, Car, ShieldCheck } from "lucide-react";
import { COMPANY } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_UI = {
  pending: { label: "Pending review", icon: Clock, cls: "text-[#F2B705] border-[#F2B705]/40 bg-[#F2B705]/10" },
  approved: { label: "Approved", icon: CheckCircle2, cls: "text-green-400 border-green-500/40 bg-green-500/10" },
  rejected: { label: "Not approved", icon: XCircle, cls: "text-red-400 border-red-500/40 bg-red-500/10" },
};

function UploadField({ label, onUploaded, testid }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(data.path);
      setDone(true);
    } catch {
      toast.error("Upload failed. Try a smaller image.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <label data-testid={testid} className={`flex items-center justify-between gap-2 cursor-pointer border-2 border-dashed rounded-lg px-4 py-3 transition-colors ${done ? "border-green-500/50 bg-green-500/5" : "border-white/15 hover:border-[#F2B705]/50"}`}>
      <span className="text-sm text-white/70 flex items-center gap-2">
        {busy ? <Loader2 className="animate-spin" size={16} /> : done ? <CheckCircle2 className="text-green-400" size={16} /> : <Upload size={16} />}
        {done ? `${label} uploaded` : label}
      </span>
      <input type="file" accept="image/*" className="hidden" onChange={handle} />
    </label>
  );
}

function LookupForm() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", code: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/driver-portal/lookup`, form);
      navigate(`/drivers/${data.token}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No booking found. Check your details.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="driver-lookup-form">
      <p className="text-white/55">Enter the email and confirmation code from your booking to add authorized drivers.</p>
      <input data-testid="driver-lookup-email" className="w-full bg-transparent border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-3 placeholder:text-white/30" placeholder="Booking email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
      <input data-testid="driver-lookup-code" className="w-full bg-transparent border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-3 placeholder:text-white/30" placeholder="Confirmation code (e.g. 24C5B2FF) *" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
      <button data-testid="driver-lookup-submit" type="submit" disabled={loading} className="w-full bg-[#F2B705] text-black font-bold py-3.5 rounded-md hover:bg-white transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
        {loading && <Loader2 className="animate-spin" size={18} />} Find My Booking
      </button>
    </form>
  );
}

export default function DriverPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(!!token);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", date_of_birth: "", dl_number: "", dl_state: "", dl_expiration: "", license_photo_path: null, selfie_path: null });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const fetchData = () => axios.get(`${API}/driver-portal/${token}`)
    .then((r) => setData(r.data))
    .catch(() => setData(false))
    .finally(() => setLoading(false));

  useEffect(() => { if (token) fetchData(); /* eslint-disable-next-line */ }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Please enter the driver's full name."); return; }
    if (!form.license_photo_path || !form.selfie_path) { toast.error("Please upload both the license photo and a selfie."); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/driver-portal/${token}/drivers`, form);
      toast.success("Driver submitted for approval!");
      setForm({ name: "", date_of_birth: "", dl_number: "", dl_state: "", dl_expiration: "", license_photo_path: null, selfie_path: null });
      setShowForm(false);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit driver.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col items-center px-5 py-10">
      <div className="w-full max-w-md">
        <Link to="/" data-testid="driverportal-home" className="inline-flex items-center gap-2 text-white/50 hover:text-[#F2B705] text-sm mb-6 transition-colors">
          <ArrowLeft size={16} /> Back to site
        </Link>
        <div className="flex items-center gap-3">
          <img src={COMPANY.logo} alt="Auto-Hire STL" className="h-14 w-14 object-contain rounded-md" />
          <div className="font-display font-extrabold text-2xl">AutoHire <span className="text-gold-gradient">STL</span></div>
        </div>

        <div className="mt-8 bg-[#0C0C0C] border border-white/10 rounded-2xl p-7" data-testid="driverportal-card">
          <h1 className="font-display font-black text-2xl flex items-center gap-2"><ShieldCheck className="text-[#F2B705]" size={24} /> Authorized Drivers</h1>

          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="animate-spin text-[#F2B705]" /></div>
          ) : !token ? (
            <div className="mt-5"><LookupForm /></div>
          ) : data === false ? (
            <div className="mt-5" data-testid="driverportal-invalid">
              <p className="text-white/60 mb-5">This link is invalid or expired. Look up your booking instead:</p>
              <LookupForm />
            </div>
          ) : (
            <>
              <div className="mt-4 bg-[#0A0A0A] border border-white/10 rounded-lg p-4 text-sm" data-testid="driverportal-booking">
                <div className="flex items-center gap-2 font-semibold"><Car size={15} className="text-[#F2B705]" /> {data.booking.vehicle}</div>
                <div className="text-white/50 mt-1">{data.booking.renter_name} · {data.booking.pickup_date} → {data.booking.return_date}</div>
                <div className="text-white/35 text-xs mt-1">Confirmation: {data.booking.confirmation}</div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <a data-testid="portal-rebook" href={`/rebook/${token}?src=portal`}
                    className="text-xs font-bold px-3 py-2 rounded-md bg-[#F2B705] text-black hover:bg-white transition-colors cursor-pointer">Request Another Rental</a>
                  <button data-testid="portal-keep-vehicle" onClick={() => axios.get(`${API}/rebooking/context/${token}?src=portal`).then((r) => { window.location.href = r.data.extend_url.replace(/^https?:\/\/[^/]+/, ""); }).catch(() => toast.error("Could not open the extension page."))}
                    className="text-xs font-bold px-3 py-2 rounded-md border border-[#F2B705]/50 text-[#F2B705] hover:bg-[#F2B705]/10 transition-colors">Request to Keep This Vehicle</button>
                  <button data-testid="portal-schedule-return" onClick={() => { window.location.href = `/rebook/${token}?mode=return&src=portal`; }}
                    className="text-xs font-bold px-3 py-2 rounded-md border border-white/20 text-white/70 hover:bg-white/5 transition-colors">Schedule My Return</button>
                </div>
              </div>

              {data.drivers.length > 0 && (
                <div className="mt-5 space-y-2" data-testid="driverportal-list">
                  {data.drivers.map((d) => {
                    const ui = STATUS_UI[d.status] || STATUS_UI.pending;
                    const Icon = ui.icon;
                    return (
                      <div key={d.id} data-testid={`driverportal-driver-${d.id}`} className="flex items-center justify-between bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-2.5">
                        <span className="font-medium">{d.name}</span>
                        <span className={`text-xs px-2.5 py-1 rounded-full border flex items-center gap-1 ${ui.cls}`}><Icon size={12} /> {ui.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              <p className="text-white/40 text-xs mt-4">{data.slots_remaining} of {data.max_drivers} driver slots remaining.</p>

              {data.slots_remaining > 0 && !showForm && (
                <button data-testid="driverportal-add-btn" onClick={() => setShowForm(true)}
                  className="mt-4 w-full border border-[#F2B705]/50 text-[#F2B705] font-semibold py-3 rounded-md hover:bg-[#F2B705] hover:text-black transition-colors flex items-center justify-center gap-2">
                  <UserPlus size={18} /> Add an Authorized Driver
                </button>
              )}

              {data.slots_remaining === 0 && (
                <p className="mt-4 text-center text-white/50 text-sm" data-testid="driverportal-full">You've reached the maximum of {data.max_drivers} additional drivers.</p>
              )}

              {showForm && (
                <form onSubmit={submit} className="mt-5 space-y-4" data-testid="driverportal-form">
                  <input data-testid="driver-name" className="w-full bg-transparent border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-2.5 placeholder:text-white/30" placeholder="Full name *" value={form.name} onChange={(e) => set("name", e.target.value)} />
                  <input data-testid="driver-dob" type="date" className="w-full bg-transparent border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-2.5 text-white/80" value={form.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} />
                  <input data-testid="driver-dl" className="w-full bg-transparent border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-2.5 placeholder:text-white/30" placeholder="Driver's license number" value={form.dl_number} onChange={(e) => set("dl_number", e.target.value)} />
                  <div className="grid grid-cols-2 gap-3">
                    <input data-testid="driver-dl-state" className="w-full bg-transparent border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-2.5 placeholder:text-white/30" placeholder="License state" value={form.dl_state} onChange={(e) => set("dl_state", e.target.value)} />
                    <input data-testid="driver-dl-exp" type="date" className="w-full bg-transparent border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-2.5 text-white/80" value={form.dl_expiration} onChange={(e) => set("dl_expiration", e.target.value)} />
                  </div>
                  <UploadField label="Driver's license photo *" testid="driver-upload-license" onUploaded={(p) => set("license_photo_path", p)} />
                  <UploadField label="Selfie *" testid="driver-upload-selfie" onUploaded={(p) => set("selfie_path", p)} />
                  <div className="flex gap-2 pt-1">
                    <button data-testid="driver-submit" type="submit" disabled={submitting} className="flex-1 bg-[#F2B705] text-black font-bold py-3 rounded-md hover:bg-white transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                      {submitting && <Loader2 className="animate-spin" size={18} />} Submit for Approval
                    </button>
                    <button data-testid="driver-cancel" type="button" onClick={() => setShowForm(false)} className="px-4 border border-white/15 rounded-md text-white/60 hover:text-white">Cancel</button>
                  </div>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
